import React, { useState, useEffect, useCallback, useContext } from 'react';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiRefreshCcw, FiDownloadCloud } from "react-icons/fi";
import { TabContext } from '../../components/TabbedLayout';
import { ReportEmbedContext } from '../reportEmbedContext';
import { BASE_URL, STOCK_LEDGER_REPORT } from '../../utlilities/config';
import axios from 'axios';
import * as XLSX from 'xlsx';
import HighlightMetricsModal from './components/HighlightMetricsModal';


// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
            <p className="text-indigo-600 font-medium">Loading...</p>
        </div>
    </div>
);

// Helper function to format date in local timezone (IST)
const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Build initial filters
const buildInitialFilters = (filters = []) => {
    const today = new Date();
    const todayFormatted = formatLocalDate(today);

    return filters.reduce((accumulator, filter) => {
        if (filter.type === 'date') {
            // Set both fromDate and toDate to today by default
            const dateValue = todayFormatted;
            return { ...accumulator, [filter.id]: dateValue };
        }

        if (filter.type === 'select') {
            const defaultValue = filter.options?.[0]?.value ?? '';
            return { ...accumulator, [filter.id]: defaultValue };
        }

        return { ...accumulator, [filter.id]: '' };
    }, {});
};


// Report configuration
const reportConfig = {
    filters: [
        { id: 'fromDate', type: 'date', label: 'From Date' },
        { id: 'toDate', type: 'date', label: 'To Date' },
    ],
    table: {
        columns: [
            { key: 'Item_Name', label: 'Item Name' },
            { key: 'OpeningQty', label: 'Opening Qty' },
            { key: 'OpeningValue', label: 'Opening Value' },
            { key: 'InwardQty', label: 'Inward Qty' },
            { key: 'OutwardQty', label: 'Outward Qty' },
            { key: 'ClosingQty', label: 'Closing Qty' },
            { key: 'ClosingValue', label: 'Closing Value' },
        ],
    },
};

export const StockLedger = () => {
    const tabContext = useContext(TabContext);
    const embedded = useContext(ReportEmbedContext);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => buildInitialFilters(reportConfig.filters));
    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [stockData, setStockData] = useState([]);
    const token = localStorage.getItem('accessToken');



    const handleFilterChange = (filterId, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterId]: value,
        }));
    };

    const handleRunReport = () => {
        getPurchaseReport();
    };

    const handleExport = () => {
        if (stockData.length === 0) {
            toast.error('No data to export', { autoClose: 2000 });
            return;
        }
        exportToExcel();
    };

    const exportToExcel = () => {
        try {
            // Get firm details
            const firmName = localStorage.getItem('firm_name') || '';
            
            // Format dates for display
            const formatDateDisplay = (date) => {
                if (!date) return 'All';
                const d = new Date(date);
                return d.toLocaleDateString('en-GB');
            };
            
            const fromDateStr = formatDateDisplay(filters.fromDate);
            const toDateStr = formatDateDisplay(filters.toDate);
            
            // Prepare Excel data as array of arrays
            const excelRows = [];
            
            // Add firm details header rows
            if (firmName) {
                excelRows.push([firmName.toUpperCase()]);
                excelRows.push([]); // Empty row
            }
            
            // Add report title
            excelRows.push(['STOCK LEDGER ITEM WISE REPORT']);
            excelRows.push([]); // Empty row
            
            // Add date range
            excelRows.push([`Period: ${fromDateStr} to ${toDateStr}`]);
            excelRows.push([]); // Empty row
            
            // Add column headers
            excelRows.push([
                'Item Name',
                'Opening Qty',
                'Opening Value',
                'Inward Qty',
                'Outward Qty',
                'Closing Qty',
                'Closing Value',
            ]);
            
            // Add data rows
            stockData.forEach((row) => {
                excelRows.push([
                    row.Item_Name || 'N/A',
                    Number(parseFloat(row.OpeningQty || 0).toFixed(2)),
                    Number(parseFloat(row.OpeningValue || 0).toFixed(2)),
                    Number(parseFloat(row.InwardQty || 0).toFixed(2)),
                    Number(parseFloat(row.OutwardQty || 0).toFixed(2)),
                    Number(parseFloat(row.ClosingQty || 0).toFixed(2)),
                    Number(parseFloat(row.ClosingValue || 0).toFixed(2)),
                ]);
            });
            
            // Add summary row
            excelRows.push([]); // Empty row
            excelRows.push([
                'TOTAL',
                Number(stockData.reduce((sum, item) => sum + parseFloat(item.OpeningQty || 0), 0).toFixed(2)),
                Number(stockData.reduce((sum, item) => sum + parseFloat(item.OpeningValue || 0), 0).toFixed(2)),
                Number(stockData.reduce((sum, item) => sum + parseFloat(item.InwardQty || 0), 0).toFixed(2)),
                Number(stockData.reduce((sum, item) => sum + parseFloat(item.OutwardQty || 0), 0).toFixed(2)),
                Number(stockData.reduce((sum, item) => sum + parseFloat(item.ClosingQty || 0), 0).toFixed(2)),
                Number(stockData.reduce((sum, item) => sum + parseFloat(item.ClosingValue || 0), 0).toFixed(2)),
            ]);
            
            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet(excelRows);
            
            // Set column widths
            const colWidths = [
                { wch: 40 },  // Item Name
                { wch: 15 },  // Opening Qty
                { wch: 15 },  // Opening Value
                { wch: 15 },  // Inward Qty
                { wch: 15 },  // Outward Qty
                { wch: 15 },  // Closing Qty
                { wch: 15 },  // Closing Value
            ];
            ws['!cols'] = colWidths;
            
            // Merge cells for title and firm name (centered across all columns)
            const merges = [];
            let currentRow = 0;
            
            if (firmName) {
                // Merge firm name row (row 0)
                merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 6 } });
                currentRow += 2; // Skip empty row
                // Merge title row
                merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 6 } });
                currentRow += 2; // Skip empty row
                // Merge date range row
                merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 6 } });
            } else {
                // Merge title row (row 0)
                merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 6 } });
                currentRow += 2; // Skip empty row
                // Merge date range row
                merges.push({ s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 6 } });
            }
            
            ws['!merges'] = merges;
            
            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Stock Ledger');
            
            // Generate filename with date range
            const fromDateFile = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const toDateFile = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const filename = `Stock_Ledger_${fromDateFile}_to_${toDateFile}.xlsx`;
            
            // Write file
            XLSX.writeFile(wb, filename);
            toast.success(`Excel file exported successfully with ${stockData.length} records`, { autoClose: 3000 });
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Failed to export to Excel', { autoClose: 2000 });
        }
    };


    // Process stock data: filter Pharmacy items and group by Item_No
    const processStockData = (rawData) => {
        if (!rawData || !rawData.stockData) return [];

        // Filter only Pharmacy items and exclude batches with null Item_Name or negative stock
        const pharmacyItems = rawData.stockData.filter(item => {
            // Must be Pharmacy type
            if (item.ItemType !== 'Pharmacy') return false;
            
            // Exclude if Item_Name is null or empty
            if (!item.Item_Name || item.Item_Name.trim() === '') return false;
            
            // Exclude if any quantity is negative
            const openingQty = parseFloat(item.OpeningQty || 0);
            const closingQty = parseFloat(item.ClosingQty || 0);
            const inwardQty = parseFloat(item.InwardQty || 0);
            const outwardQty = parseFloat(item.OutwardQty || 0);
            
            if (openingQty < 0 || closingQty < 0 || inwardQty < 0 || outwardQty < 0) {
                return false;
            }
            
            return true;
        });

        // Group by Item_No and aggregate quantities and values
        const groupedData = pharmacyItems.reduce((acc, item) => {
            const itemNo = item.Item_No;
            
            if (!acc[itemNo]) {
                acc[itemNo] = {
                    Item_No: itemNo,
                    Item_Name: item.Item_Name || 'N/A',
                    OpeningQty: 0,
                    OpeningValue: 0,
                    InwardQty: 0,
                    OutwardQty: 0,
                    ClosingQty: 0,
                    ClosingValue: 0,
                };
            }

            // Aggregate quantities and values
            acc[itemNo].OpeningQty += parseFloat(item.OpeningQty || 0);
            acc[itemNo].OpeningValue += parseFloat(item.OpeningValue || 0);
            acc[itemNo].InwardQty += parseFloat(item.InwardQty || 0);
            acc[itemNo].OutwardQty += parseFloat(item.OutwardQty || 0);
            acc[itemNo].ClosingQty += parseFloat(item.ClosingQty || 0);
            acc[itemNo].ClosingValue += parseFloat(item.ClosingValue || 0);

            return acc;
        }, {});

        // Convert object to array
        return Object.values(groupedData);
    };

    const getPurchaseReport = useCallback(async () => {
        setLoading(true);

        try {
            // Convert date strings to Date objects and format as YYYY-MM-DD
            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;

            // Format date as YYYY-MM-DD
            const formatDate = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            const response = await axios.get(`${BASE_URL}${STOCK_LEDGER_REPORT}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'pageNo': 1,
                    'pageSize': 1000000,
                    'departmentID': localStorage.getItem('department_id') || '',
                },
            });
            
            if (response.status === 200 && response.data.isSucess) {
                const processedData = processStockData(response.data.data);
                console.log(processedData);
                setStockData(processedData);
            } else {
                toast.error('Failed to fetch stock ledger report', { autoClose: 1000 });
            }
        } catch (error) {
            console.error('Failed to fetch stock ledger report:', error);
            toast.error('Failed to fetch stock ledger report', { autoClose: 1000 });
        } finally {
            setLoading(false);
        }
    }, [filters, token]);

    // Auto-fetch on component mount
    useEffect(() => {
        getPurchaseReport();
    }, []);

    return (
        <div className="flex h-full bg-background">
            {/* <LeftMenu /> */}
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />

            {loading && <Spinner />}

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
                    <div className={`flex flex-col gap-3 ${embedded ? 'lg:flex-row lg:justify-end' : 'lg:flex-row lg:items-center lg:justify-between'}`}>
                        {!embedded && (
                            <div className="min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                    Stock Ledger Reports
                                </p>
                                <h1 className="text-lg font-bold text-slate-800">
                                    Stock Ledger Item Wise
                                </h1>
                            </div>
                        )}
                        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={handleRunReport}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
                            >
                                <FiRefreshCcw size={16} />
                                Run report
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setFilters(buildInitialFilters(reportConfig.filters));
                                    setStockData([]);
                                }}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                <FiDownloadCloud size={16} />
                                Export
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex flex-col gap-3 flex-1 min-h-0">
                        {/* Filters Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex-shrink-0">
                            <form className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                                {reportConfig.filters.map((filter) => {
                                    if (filter.type === 'date') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-2 text-sm font-medium text-slate-700">
                                                {filter.label}
                                                <input
                                                    type="date"
                                                    value={filters[filter.id] ?? ''}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                                />
                                            </label>
                                        );
                                    }
                                    return null;
                                })}
                            </form>
                        </section>

                        <HighlightMetricsModal
                            isOpen={isMetricsModalOpen}
                            onOpen={() => setIsMetricsModalOpen(true)}
                            onClose={() => setIsMetricsModalOpen(false)}
                        >
                            <div className="grid gap-4 md:grid-cols-6">
                                <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Items
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">{stockData.length}</p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Opening Value
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹{stockData.reduce((sum, item) => sum + (parseFloat(item.OpeningValue || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total In Stock
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        {stockData.reduce((sum, item) => sum + (parseFloat(item.InwardQty || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Out Stock
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        {stockData.reduce((sum, item) => sum + (parseFloat(item.OutwardQty || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Closing Value
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        ₹{stockData.reduce((sum, item) => sum + (parseFloat(item.ClosingValue || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                                <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Closing Qty
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">
                                        {stockData.reduce((sum, item) => sum + (parseFloat(item.ClosingQty || 0)), 0).toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        </HighlightMetricsModal>

                        {/* Table Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm flex flex-col flex-1 min-h-0">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0">
                                <div>
                                    <p className="text-sm text-slate-500">
                                        Showing <span className="font-semibold text-slate-700">{stockData.length}</span> pharmacy items for this report.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-2 flex-1 overflow-auto min-h-0 rounded-xl border border-slate-200">
                                <table className="min-w-full divide-y divide-slate-200 text-sm text-slate-700">
                                    <thead className="bg-gray-200 sticky top-0 z-10">
                                        <tr>
                                            {reportConfig.table.columns.map((column) => (
                                                <th
                                                    key={column.key}
                                                    scope="col"
                                                    className="px-2 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap"
                                                >
                                                    {column.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {stockData.length > 0 ? (
                                            stockData.map((row, index) => (
                                                <tr key={row.Item_No || index} className="hover:bg-indigo-50/30">
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{row.Item_Name || 'N/A'}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">{parseFloat(row.OpeningQty || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.OpeningValue || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">{parseFloat(row.InwardQty || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">{parseFloat(row.OutwardQty || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">{parseFloat(row.ClosingQty || 0).toFixed(2)}</td>
                                                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap text-left">₹{parseFloat(row.ClosingValue || 0).toFixed(2)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={reportConfig.table.columns.length} className="px-4 py-8 text-center text-slate-500">
                                                    No data available for the selected period
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    </div>
                </main>
            </div>

        </div>
    );
};

export default withAuth(StockLedger);

